/** Small shared pieces: banners, empty states, spinners, error rendering. */

import type { ReactNode } from 'react'
import { ApiError } from '../api'

export function Spinner({ label }: { label?: string }) {
  return (
    <span className="loading-row">
      <span className="spinner" aria-hidden="true" />
      <span>{label ?? 'Loading…'}</span>
    </span>
  )
}

export function Banner({
  tone = 'info',
  title,
  children,
}: {
  tone?: 'info' | 'error' | 'warn'
  title: string
  children?: ReactNode
}) {
  return (
    <div className={`banner banner--${tone}`} role={tone === 'error' ? 'alert' : undefined}>
      <div>
        <div className="banner__title">{title}</div>
        {children ? <p className="banner__body">{children}</p> : null}
      </div>
    </div>
  )
}

export function EmptyState({ title, children }: { title: string; children?: ReactNode }) {
  return (
    <div className="empty">
      <div className="empty__title">{title}</div>
      {children ? <div>{children}</div> : null}
    </div>
  )
}

/**
 * Turns whatever came back into something worth reading.
 *
 * 403 gets its own wording because it is the one people hit legitimately: the
 * SPA hides screens a user holds no grant for, but a deep link or a stale tab
 * reaches the API anyway, and "you do not have access" is more use than the raw
 * message.
 */
export function ErrorBanner({ error, context }: { error: unknown; context?: string }) {
  if (!error) return null

  if (error instanceof ApiError) {
    if (error.status === 403) {
      return (
        <Banner tone="warn" title="You do not have access to this">
          Your sign-in worked, but no permission has been granted for it. An admin can
          grant it on the Access screen — permissions are per config type, so being able
          to see one screen does not imply the others.
        </Banner>
      )
    }
    if (error.status === 401) {
      return (
        <Banner tone="warn" title="Your session has expired">
          Reload the page to sign in again.
        </Banner>
      )
    }
    return (
      <Banner tone="error" title={error.message}>
        {error.detail ?? context}
      </Banner>
    )
  }

  return (
    <Banner tone="error" title={context ?? 'Something went wrong'}>
      {error instanceof Error ? error.message : String(error)}
    </Banner>
  )
}
